import React from 'react';
import Header from './HeaderHome';
import Footer from './FooterHome';

const ChildWelfare = () => {
    // ── Key Programmes data ──
    const programmes = [
        {
            icon: "📚",
            title: "Education Support",
            desc: "Providing scholarships, school fees, books, uniforms, stationery, and digital learning resources to ensure every child can attend school and stay motivated.",
        },
        {
            icon: "🥗",
            title: "Nutrition & Health",
            desc: "Running midday meal programmes, providing nutritional supplements, organising health check-ups, and supporting medical treatment for sick children.",
        },
        {
            icon: "🛡️",
            title: "Child Protection",
            desc: "Rescuing children from child labour, trafficking, and abuse; providing safe shelter, counselling, and legal aid to ensure their rights are upheld.",
        },
        {
            icon: "🧸",
            title: "Early Childhood Care",
            desc: "Running creches and preschools that provide a safe, stimulating environment for young children, including nutrition, early learning, and play.",
        },
        {
            icon: "🔧",
            title: "Skills & Vocational Training",
            desc: "Equipping older children and adolescents with life skills, computer literacy, and vocational training to make them self-reliant and employable.",
        },
        {
            icon: "📢",
            title: "Awareness & Advocacy",
            desc: "Conducting community awareness programmes on child rights, hygiene, nutrition, and the importance of education, and advocating for policy changes.",
        },
    ];

    // ── Impact Stats ──
    const stats = [
        { number: "3,200+", label: "Children Supported" },
        { number: "1,500+", label: "Scholarships Awarded" },
        { number: "2,100+", label: "Health Check-ups" },
        { number: "800+", label: "Children Protected" },
    ];

    // ── How You Can Help ──
    const helpOptions = [
        { icon: "🎓", title: "Sponsor Education", desc: "Fund a child's school fees, books, and supplies." },
        { icon: "🍲", title: "Provide Meals", desc: "Support our nutrition programmes for children." },
        { icon: "❤️", title: "Volunteer", desc: "Mentor, teach, or spend time with children." },
    ];

    return (
        <>
            <Header />

            {/* ========== HERO ========== */}
            <section className="child-hero relative text-white py-16">
                <div className="container mx-auto px-4 text-center">
                    <div className="max-w-3xl mx-auto">
                        <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
                            <i className="fas fa-child mr-2"></i> Our Service
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
                            Child Welfare
                        </h1>
                        <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
                            Nurturing young minds, protecting their rights, and building a brighter tomorrow
                        </p>
                        <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
                    </div>
                </div>
            </section>

            {/* ========== OVERVIEW ========== */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
                        <div className="text-center mb-10">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-800">Protecting &amp; Empowering Children</h2>
                            <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
                        </div>
                        <p className="text-gray-700 text-lg leading-relaxed mb-6">
                            Every child deserves a safe, loving, and nurturing environment to grow, learn, and thrive. Yet,
                            millions of children in India face poverty, malnutrition, lack of education, exploitation, and
                            neglect. At Venkatadri Seva Trust, our <strong>Child Welfare</strong> programme is dedicated to
                            breaking this cycle of hardship by providing holistic support to underprivileged children.
                        </p>
                        <p className="text-gray-700 text-lg leading-relaxed">
                            We work tirelessly to ensure that children have access to quality education, proper nutrition,
                            healthcare, and protection from abuse and exploitation. Our efforts are focused on empowering
                            children to realise their full potential and become confident, responsible citizens of tomorrow.
                        </p>
                    </div>
                </div>
            </section>

            {/* ========== VISION & MISSION ========== */}
            <section className="py-16 bg-primary">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        <div className="vision-card rounded-2xl p-8 text-white text-center">
                            <div className="text-5xl mb-4">👁️</div>
                            <h3 className="text-2xl font-bold mb-3">Our Vision</h3>
                            <p className="text-yellow-50/90 text-lg leading-relaxed">
                                A world where every child is cherished, protected, and given equal opportunities to learn, grow,
                                and live with dignity—free from poverty, hunger, and fear.
                            </p>
                        </div>
                        <div className="vision-card rounded-2xl p-8 text-white text-center">
                            <div className="text-5xl mb-4">🎯</div>
                            <h3 className="text-2xl font-bold mb-3">Our Mission</h3>
                            <p className="text-yellow-50/90 text-lg leading-relaxed">
                                To rescue children from vulnerable situations, provide them with education, nutrition,
                                healthcare, and emotional support, and advocate for their rights at every level.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== KEY PROGRAMMES ========== */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-white">Our Key Programmes</h2>
                        <p className="text-yellow-100 mt-2 text-lg">
                            Giving children the foundation they need to build a bright future
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {programmes.map((programme, idx) => (
                            <div
                                key={idx}
                                className="program-card bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary"
                            >
                                <div className="service-icon"><span>{programme.icon}</span></div>
                                <h3 className="text-xl font-semibold text-gray-800">{programme.title}</h3>
                                <p className="text-gray-600 mt-2 text-sm">{programme.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ========== IMPACT / STATS ========== */}
            {/* <section className="py-16 bg-primary">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-white">Our Impact</h2>
                        <div className="w-20 h-1 bg-yellow-300 mx-auto mt-3 rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center text-white">
                        {stats.map((stat, idx) => (
                            <div key={idx}>
                                <div className="text-4xl md:text-5xl font-bold text-yellow-300">{stat.number}</div>
                                <p className="text-yellow-100/80 text-sm mt-1">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            {/* ========== HOW YOU CAN HELP ========== */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-800">How You Can Help</h2>
                            <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
                            <p className="text-gray-600 mt-4 text-lg">Your support can change a child's life forever.</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {helpOptions.map((option, idx) => (
                                <div key={idx} className="text-center p-4 rounded-lg bg-gray-50 hover:bg-yellow-50 transition-smooth">
                                    <div className="text-4xl text-primary mb-3">{option.icon}</div>
                                    <h4 className="font-bold text-gray-800">{option.title}</h4>
                                    <p className="text-gray-600 text-sm mt-1">{option.desc}</p>
                                </div>
                            ))}
                        </div>

                        <div className="text-center mt-8">
                            <a
                                href="/donate"
                                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-smooth inline-block"
                            >
                                <i className="fas fa-hand-holding-heart mr-2"></i> Support a Child Today
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== TESTIMONIAL ========== */}
            <section className="py-16 bg-primary">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center text-white">
                        <div className="text-6xl mb-4">“</div>
                        <p className="text-xl md:text-2xl font-light leading-relaxed italic">
                            Before Venkatadri Seva Trust, I had no hope of continuing my studies. They gave me a scholarship,
                            books, and even a mentor who guided me. Today, I am the first in my family to go to college. I will
                            never forget their kindness.
                        </p>
                        <div className="mt-6">
                            <p className="font-semibold text-yellow-200">— Priya, 18</p>
                            <p className="text-yellow-100/70 text-sm">Bengaluru</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== JOIN OUR MISSION ========== */}
            <section className="py-16">
                <div className="container mx-auto px-4 text-center text-white">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Mission</h2>
                        <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
                        <p className="text-xl text-yellow-100/90 italic mb-6">
                            "Every child deserves a chance to dream, and every dream deserves a chance to come true."
                        </p>
                        <p className="text-lg text-white/80 leading-relaxed mb-8">
                            Your generous support—through donations, volunteering, or partnerships—helps us reach more children
                            and build a brighter future for the next generation.
                        </p>
                        <p className="text-xl text-yellow-100 font-light mb-8">
                            Together, let us make compassion a way of life and create a better world for generations to come.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <a
                                href="/donate"
                                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-smooth"
                            >
                                <i className="fas fa-heart mr-2"></i> Donate Now
                            </a>
                            <a
                                href="/volunteer"
                                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-smooth"
                            >
                                <i className="fas fa-hand-peace mr-2"></i> Volunteer
                            </a>

                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </>
    );
};

export default ChildWelfare;